import { JumpTarget } from 'config/loanJumpTargets'
import { defaultInputs as achInputs } from 'pages/Loan/Servicing/ACH/constants'
import { defaultInputs as loanPaymentsInputs } from 'pages/Loan/Servicing/LoanPayments/constants'
import { defaultInputs as payoffInputs } from 'pages/Loan/Servicing/Payoff/constants'
import { defaultInputs as penaltiesInputs } from 'pages/Loan/Servicing/Penalties/constants'
import {
  additionalInputs as trustAddlInputs,
  defaultInputs as trustLedgerInputs,
  interestDefaultInputs as subLedgerInputs,
} from 'pages/Loan/Servicing/TrustLedger/constants'

type FieldMap = Record<string, any>

// Static servicing screens whose fields are defined in a co-located constants.ts.
const STATIC_SOURCES: { menu: string; group: string; build: () => FieldMap }[] = [
  { menu: 'penalties', group: 'Penalties (Servicing)', build: () => penaltiesInputs() },
  { menu: 'payoff', group: 'Payoff (Servicing)', build: () => payoffInputs() },
  { menu: 'ach', group: 'ACH (Servicing)', build: () => achInputs() },
  { menu: 'loanPayments', group: 'Loan Payments (Servicing)', build: () => loanPaymentsInputs(0) },
  {
    menu: 'trustLedger',
    group: 'Trust Ledger (Servicing)',
    build: () => ({ ...trustLedgerInputs(), ...trustAddlInputs(), ...subLedgerInputs() }),
  },
]

const fieldsToTargets = (fields: FieldMap, menu: string, group: string, seen: Set<string>): JumpTarget[] => {
  const out: JumpTarget[] = []
  Object.keys(fields || {}).forEach((key) => {
    const f = fields[key]
    const title = String(f?.title || '').trim()
    if (!title) return
    const dk = `${menu}:${title.toLowerCase()}`
    if (seen.has(dk)) return
    seen.add(dk)
    out.push({
      id: `sf-${menu}-${key}`,
      label: title,
      group,
      keywords: `servicing field ${key}`,
      to: (n) => `/servicing/${n}?menu=${menu}`,
    })
  })
  return out
}

// Field labels from the statically-defined servicing screens.
export const getServicingStaticFieldTargets = (): JumpTarget[] => {
  const seen = new Set<string>()
  const out: JumpTarget[] = []
  for (const s of STATIC_SOURCES) {
    let fields: FieldMap = {}
    try {
      fields = s.build() || {}
    } catch {
      continue
    }
    out.push(...fieldsToTargets(fields, s.menu, s.group, seen))
  }
  return out
}

// Servicing Information fields are returned by the backend per loan (getServicingNotes()).
export const servicingInfoResToTargets = (res: any): JumpTarget[] => {
  if (!res) return []
  const seen = new Set<string>()
  const maps: FieldMap[] = [res.inputs, res.taxInputs, res.insuranceInputs, res.extensionInputs].filter(Boolean)
  const out: JumpTarget[] = []
  maps.forEach((m) => out.push(...fieldsToTargets(m, 'servicingNotes', 'Servicing Information', seen)))
  return out
}
